import React, { useState, useRef, useCallback, useEffect } from 'react';
import AutoCompleteWidget from './widgets/AutoCompleteWidget';
import CheckBoxWidget from './widgets/CheckBoxWidget';
import ImageWidget from './widgets/ImageWidget';
import ParagraphWidget from './widgets/ParagraphWidget';
import RadioWidget from './widgets/RadioWidget';
import TextWidget from './widgets/TextWidget';
import TitleWidget from './widgets/TitleWidget';
import WidgetConfig from './WidgetConfig';
import RadioAutoCompleteWidget from './widgets/RadioAutoCompleteWidget';
import RadioTextWidget from './widgets/RadioTextWidget';
import ListWidget from './widgets/ListWidget';
import RadioListWidget from './widgets/RadioListWidget';
import { WIDGET_TYPES } from '../../../../collums-constants';
import { useForms } from '../../../hooks/forms';
import TextAreaWidget from './widgets/TextAreaWidget';
import { RenderViewStyles } from './styles';
import { Grid, IconButton, withStyles, makeStyles } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import PropTypes from 'prop-types';
import { useDrag, useDrop } from 'react-dnd';
import BlockWidget from './widgets/BlockWidget';
import Button from '@material-ui/core/Button';
import { modalsButtonStyles } from '../../../../collums-constants/styles/stylesheets/buttonsStyles';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import CancelContinueModal from '../../common/CancelContinueModal';
import './placeholderStyles.css';
import './styles.css';
import SignatureWidget from './widgets/SignatureWidget';
import PractitionerSignatureWidget from './widgets/PractitionerSignatureWidget';
import { hasErrors } from '../../../helpers';

const FORM_FIELD_DND_TYPE = 'FORM_FIELD';

const SCROLL_ZONE_SIZE = 60;
const SCROLL_SPEED = 12;

const getAllScrollParents = node => {
    const parents = [];
    let current = node?.parentElement;
    while (current) {
        const style = window.getComputedStyle(current);
        if (/(auto|scroll)/.test(style.overflowY) && current.scrollHeight > current.clientHeight) {
            parents.push(current);
        }
        current = current.parentElement;
    }
    return parents;
};

const useAutoScrollOnDrag = containerRef => {
    useEffect(() => {
        if (!containerRef) return;
        const container = containerRef.current;
        if (!container) return;

        const handleDragOver = e => {
            const scrollParents = getAllScrollParents(container);
            for (const parent of scrollParents) {
                const rect = parent.getBoundingClientRect();
                if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
                    if (e.clientY - rect.top < SCROLL_ZONE_SIZE && parent.scrollTop > 0) {
                        parent.scrollTop -= SCROLL_SPEED;
                        break;
                    } else if (
                        rect.bottom - e.clientY < SCROLL_ZONE_SIZE &&
                        parent.scrollTop < parent.scrollHeight - parent.clientHeight
                    ) {
                        parent.scrollTop += SCROLL_SPEED;
                        break;
                    }
                }
            }
        };

        window.addEventListener('dragover', handleDragOver);
        return () => window.removeEventListener('dragover', handleDragOver);
    }, [containerRef]);
};

const DraggableField = ({
    index,
    widgetProps,
    classes,
    isJourney,
    onGridClick,
    anchorEl,
    targetWidget,
    children,
    moveField,
    setDeletingIndex
}) => {
    const ref = useRef(null);

    const [{ isDragging }, drag, preview] = useDrag({
        item: { type: FORM_FIELD_DND_TYPE, index },
        collect: monitor => ({
            isDragging: monitor.isDragging()
        })
    });

    const [{ isOver }, drop] = useDrop({
        accept: FORM_FIELD_DND_TYPE,
        hover: (draggedItem, monitor) => {
            if (!ref.current) return;
            const dragIndex = draggedItem.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

            moveField(dragIndex, hoverIndex);
            draggedItem.index = hoverIndex;
        },
        collect: monitor => ({
            isOver: monitor.isOver()
        })
    });

    preview(drop(ref));

    const gridClickHandler = event => {
        event.preventDefault();
        event.stopPropagation();
        if (isJourney) return;
        if (anchorEl && widgetProps.id === targetWidget) return;
        onGridClick(event, widgetProps.id);
    };

    return (
        <Grid
            item
            xs={12}
            onClick={gridClickHandler}
            className={classes.widgetGrid}
            ref={ref}
            style={{
                opacity: isDragging ? 0.4 : 1,
                borderTop: isOver ? '2px solid #2b78e4' : 'none'
            }}
        >
            {!isJourney ? <div className={classes.clickHandler} onClick={gridClickHandler} /> : ''}
            <div className={`${classes.widgetArea} widget-area`}>
                {children}
                <div className={'change-order-container'} style={{ display: isJourney ? 'none' : 'flex' }}>
                    <div ref={drag} className={classes.dragHandle}>
                        <DragIndicatorIcon className={classes.arrowIcons} />
                    </div>
                </div>
                <div className={classes.deleteItemContainer} style={{ display: isJourney ? 'none' : 'flex' }}>
                    <IconButton
                        onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDeletingIndex(index);
                        }}
                    >
                        <DeleteOutlineOutlinedIcon className={classes.deleteIcon} />
                    </IconButton>
                </div>
            </div>
        </Grid>
    );
};

DraggableField.propTypes = {
    index: PropTypes.number.isRequired,
    widgetProps: PropTypes.shape({
        id: PropTypes.string
    }).isRequired,
    classes: PropTypes.object.isRequired,
    isJourney: PropTypes.bool,
    onGridClick: PropTypes.func.isRequired,
    anchorEl: PropTypes.object,
    targetWidget: PropTypes.string,
    children: PropTypes.node,
    moveField: PropTypes.func.isRequired,
    setDeletingIndex: PropTypes.func.isRequired
};

const Render = ({ classes, openPreviewModal }) => {
    const buttonClasses = makeStyles(modalsButtonStyles)();
    const [anchorEl, setAnchorEl] = useState(null);
    const [targetWidget, setTargetWidget] = useState();
    const { data, setData, isJourney, additionalProps, errors } = useForms();
    const [deletingIndex, setDeletingIndex] = useState(null);
    const formContainerRef = useRef(null);
    useAutoScrollOnDrag(isJourney ? null : formContainerRef);

    const onChange = (id, value, fieldName = 'value') => {
        setData(prev => {
            const fields = prev.fields.map(el => {
                if (el.id === id) {
                    const newData = { ...el };
                    if (fieldName !== 'value') {
                        const additionalInfo = newData.additionalInfo ? { ...newData.additionalInfo } : {};
                        additionalInfo[fieldName] = value;
                        if (fieldName === 'isVisible') {
                            newData.value = undefined;
                        }
                        newData.additionalInfo = additionalInfo;
                    }
                    newData[fieldName] = value;
                    return newData;
                }
                return el;
            });
            return { ...prev, fields };
        });
    };

    const moveField = useCallback(
        (dragIndex, hoverIndex) => {
            setData(prev => {
                const newFields = [...prev.fields];
                const [removed] = newFields.splice(dragIndex, 1);
                newFields.splice(hoverIndex, 0, removed);
                return { ...prev, fields: newFields };
            });
        },
        [setData]
    );

    const onGridClick = useCallback(
        (event, widgetId) => {
            setAnchorEl(event.currentTarget);
            setTargetWidget(widgetId);
        },
        [setAnchorEl, setTargetWidget]
    );

    const RenderComponent = (widgetProps, index) => {
        if (!widgetProps?.type) return null;
        const props = {
            widgetProps,
            onChange
        };
        if (widgetProps.type === WIDGET_TYPES.AUTOCOMPLETE_WIDGET) {
            widgetProps.limitTags = 999;
        }

        if (additionalProps && additionalProps?.fields) {
            const type = widgetProps.type;
            if (Object.prototype.hasOwnProperty.call(additionalProps.fields, type)) {
                props.widgetProps = {
                    // eslint-disable-next-line react/prop-types
                    ...props.widgetProps,
                    ...additionalProps.fields[type]
                };
            }
        }

        if (widgetProps.type === WIDGET_TYPES.PARAGRAPH_WIDGET) {
            const nextField = data.fields[index + 1];
            if (nextField && nextField.isRequired) {
                props.nextFieldRequired = true;
                props.nextFieldHasError = hasErrors(nextField.id, errors);
            }
        }

        const WidgetComponent = (() => {
            switch (widgetProps.type) {
                case WIDGET_TYPES.TEXT_WIDGET:
                    return TextWidget;
                case WIDGET_TYPES.PARAGRAPH_WIDGET:
                    return ParagraphWidget;
                case WIDGET_TYPES.CHECKBOX_WIDGET:
                    return CheckBoxWidget;
                case WIDGET_TYPES.RADIO_WIDGET:
                    return RadioWidget;
                case WIDGET_TYPES.SELECT_WIDGET:
                case WIDGET_TYPES.AUTOCOMPLETE_WIDGET:
                    return AutoCompleteWidget;
                case WIDGET_TYPES.TITLE_WIDGET:
                    return TitleWidget;
                case WIDGET_TYPES.IMAGE_WIDGET:
                    return ImageWidget;
                case WIDGET_TYPES.RADIO_AUTOCOMPLETE_WIDGET:
                    return RadioAutoCompleteWidget;
                case WIDGET_TYPES.RADIO_TEXT_WIDGET:
                    return RadioTextWidget;
                case WIDGET_TYPES.LIST_WIDGET:
                    return ListWidget;
                case WIDGET_TYPES.RADIO_LIST_WIDGET:
                    return RadioListWidget;
                case WIDGET_TYPES.TEXT_AREA_WIDGET:
                    return TextAreaWidget;
                case WIDGET_TYPES.BLOCK_WIDGET:
                    return BlockWidget;
                case WIDGET_TYPES.SIGNATURE_FIELD_WIDGET:
                    return SignatureWidget;
                case WIDGET_TYPES.PRACTITIONER_SIGNATURE_FIELD_WIDGET:
                    return PractitionerSignatureWidget;
                default:
                    return <></>;
            }
        })();

        return (
            <DraggableField
                key={widgetProps.id}
                index={index}
                widgetProps={widgetProps}
                classes={classes}
                isJourney={isJourney}
                onGridClick={onGridClick}
                anchorEl={anchorEl}
                targetWidget={targetWidget}
                moveField={moveField}
                setDeletingIndex={setDeletingIndex}
            >
                <WidgetComponent {...props} />
            </DraggableField>
        );
    };

    return (
        <>
            <div className={classes.formContent} ref={formContainerRef}>
                <Grid
                    container
                    spacing={3}
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    className={!isJourney ? classes.widgetsBuilderGridContainer : classes.widgetsGridContainer}
                >
                    {data.fields.map(RenderComponent)}
                </Grid>
                {anchorEl && <WidgetConfig widgetId={targetWidget} anchorEl={anchorEl} setAnchorEl={setAnchorEl} />}
                {!isJourney && openPreviewModal && data.fields.length !== 0 && (
                    <Button
                        className={`${buttonClasses.baseButton} ${classes.previewButton}`}
                        onClick={openPreviewModal}
                    >
                        Preview
                    </Button>
                )}
            </div>
            <CancelContinueModal
                open={typeof deletingIndex === 'number'}
                title={'Delete block'}
                contentText={'Are you sure you want to delete this block?'}
                onContinue={() => {
                    const newFields = data.fields;
                    newFields.splice(deletingIndex, 1);
                    setData({ ...data, fields: newFields });
                    setDeletingIndex(null);
                    setAnchorEl(null);
                }}
                onCancel={() => {
                    setDeletingIndex(null);
                }}
                cancelButtonText="Back"
                continueButtonText="Delete"
            />
        </>
    );
};

Render.propTypes = {
    classes: PropTypes.object.isRequired,
    openPreviewModal: PropTypes.func
};

export default withStyles(RenderViewStyles)(Render);
